import { Badge, Col, DatePicker, Flex, Input, message, Radio, Row, Table, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { IconRight } from "../../../assets/icons";
import moment from "moment";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import { getFormattedNumber } from "@utils";
import dayjs from "dayjs";
import { PRIVATE_ROUTE } from "../../../route.constants";
import OrderDetails from "../../OrderManagement/OrderDetails/OrderDetails";
import { ORDER_STATUS } from "../../../constants";
import {IconSearch } from "../../../assets/icons";
import _, { debounce } from "lodash";
import { useTranslation } from "react-i18next";

const RootContainer = styled.div`
  .ant-radio-group {
    display: flex;
    gap: 8px;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .ant-radio-button-wrapper {
    width: 25%;
    padding: 8px 12px;
    height: 48px;
    border-radius: 8px;
    border-inline-start-width: 1px;
    border: 1px solid ${(props) => props.theme.primaryColor};
    background-color: ${(props) => props.theme.backgroundColor};
    color: #1d1014;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;

    ::before {
      display: none;
    }
  }

  .ant-radio-button-wrapper-checked {
    background-color: ${(props) => props.theme.primaryColor};
    color: #fff;

    :hover {
      color: #fff;
    }
  }

  .ant-table svg {
    cursor: pointer;
  }

  .ant-picker {
    width: 100%;
    height: 48px;
  }
`;

const Title = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  font-size: 24px;
  font-weight: 700;
  color: #1e1e1e;
`;

const Card = styled.div`
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  background: ${(props) => props.theme.backgroundColor};
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #1d1014;
`;

const BoldText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d1014;
`;

const DAILY = "daily";
const MONTHLY = "monthly";
const YEARLY = "yearly";

const { HOME, REPORTS, ORDER_REPORT } = PRIVATE_ROUTE;

const dateFormat = "DD MMM YYYY";
const monthFormat = "MMMM";
const yearFormat = "YYYY";

const OrderReport = () => {
  const navigate = useNavigate();
  const { period } = useParams();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [data, setData] = useState([]);
  const [date, setDate] = useState(dayjs());
  const [search, setSearch] = useState("");

  const {t} = useTranslation();

  useEffect(() => {
    getOrderReport(period, date);

    // eslint-disable-next-line
  }, [period, date]);

  const getOrderReport = (period, date) => {
    setLoading(true);
    setData([]);
    const format =
      period === DAILY ? "YYYY-MM-DD" : period === MONTHLY ? "YYYY-MM" : "YYYY";
    api
      .getOrderReport({
        period: period,
        date: date.format(format),
        search: search,
      })
      .then(({ counts, data, reports }) => {
        setLoading(false);
        setCounts(counts);
        setData(data || reports);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error);
      });
  };

  const goToDetails = (item) => {
    navigate(`details/${item.id}`);
  };

  const handleStatusChange = (e) => {
    navigate(`/${HOME}/${REPORTS}/${ORDER_REPORT}/${e.target.value}`, {
      replace: true,
    });
  };

  const handleDateChange = (date) => {
    setDate(date);
  };

  const getOrderStatus = (status) => {
    switch (status) {
      case ORDER_STATUS.PENDING:
        return <Badge color={"orange"} text={t('pending')} />;
      case ORDER_STATUS.UNPAID:
        return <Badge color={"purple"} text={t('unpaid')} />;
      case ORDER_STATUS.CANCELLED:
        return <Badge color={"red"} text={t('cancelled')} />;
      case ORDER_STATUS.CONFIRMED:
        return <Badge color={"green"} text={t('confirmed')} />;
      case ORDER_STATUS.COMPLETED:
        return <Badge color={"blue"} text={t('complete')} />;
      default:
        return "";
    }
  };

  const daily_columns = [
    {
      key: "created_at",
      dataIndex: "created_at",
      title: t('time'),
      render: (_, record) => moment(record.created_at).format("HH:mm"),
    },
    {
      key: "order_id",
      dataIndex: "order_id",
      title: t('order_id'),
    },
    {
      key: "user",
      dataIndex: "user",
      title: t('customer_id_name'),
      render: (_, record) => (
         <Flex style={{gap:"0px",flexDirection:"column",justifyContent:"start",alignItems:"start"}}>
            <Typography>{record?.user?.custom_name}</Typography>
            <Typography>{record?.user?.username}</Typography>
          </Flex>
      ),
    },
    {
      key: "total_price",
      dataIndex: "total_price",
      title: t('quantity_amount'),
      render: (_, record) =>
        `${getFormattedNumber(
          +record.product_count - +record.cancel_count
        )} / ${getFormattedNumber(record.final_amount)}`,
    },
    {
      key: "status",
      dataIndex: "status",
      title: t('status'),
      render: (_, record) => getOrderStatus(record.status),
    },
  ];

  const monthly_columns = [
    {
      key: "date",
      dataIndex: "date",
      title: t('date'),
    },
    {
      key: "total_orders",
      dataIndex: "total_orders",
      title: t('total_orders'),
      render: (_, record) => (
      <span
        style={{ color: "#1859ff", cursor: "pointer", fontWeight: 600 }}
        onClick={() => handleRowClick(record, MONTHLY)}
      >
        {getFormattedNumber(record.total_orders)}
      </span>
    ),
    },
    {
      key: "total_price",
      dataIndex: "total_price",
      title: t('amount'),
      render: (_, record) => `${getFormattedNumber(record.total_price)}`,
    },
  ];

  const yearly_columns = [
    {
      key: "month",
      dataIndex: "month",
      title: t('month'),
    },
    {
      key: "total_orders",
      dataIndex: "total_orders",
      title: t('total_orders'),
      render: (_, record) => (
      <span
        style={{ color: "#1859ff", cursor: "pointer", fontWeight: 600 }}
        onClick={() => handleRowClick(record, YEARLY)}
      >
        {getFormattedNumber(record.total_orders)}
      </span>
    ),
    },
    {
      key: "total_price",
      dataIndex: "total_price",
      title: t('amount'),
      render: (_, record) => `${getFormattedNumber(record.total_price)}`,
    },
  ];

  const dynamicColumns =
    period === DAILY
      ? daily_columns
      : period === MONTHLY
      ? monthly_columns
      : yearly_columns;
  const actions =
    period === DAILY
      ? [
          {
            key: "action",
            title: "",
            render: (_, record) => (
              <IconRight onClick={() => goToDetails(record)} />
            ),
          },
        ]
      : [];

  const columns = [...dynamicColumns, ...actions];

  const period_options = [
    {
      key: DAILY,
      label: t('daily'),
    },
    {
      key: MONTHLY,
      label: t('monthly'),
    },
    {
      key: YEARLY,
      label: t('yearly'),
    },
  ];

  const handleRowClick = (record) => {
  if (period === YEARLY) {
    const newDate = dayjs(`${record.raw_month}`);
    navigate(`/${HOME}/${REPORTS}/${ORDER_REPORT}/${MONTHLY}`);
    setDate(newDate);
  } else if (period === MONTHLY) {
    const newDate = dayjs(record.raw_date);
    navigate(`/${HOME}/${REPORTS}/${ORDER_REPORT}/${DAILY}`);
    setDate(newDate);
  }
};


const debouncedSearch = useMemo(
  () =>
    debounce((value) => {
      setSearch(value.trim());
    }, 800), // adjust delay here
  []
);

useEffect(() => {
  return () => {
    debouncedSearch.cancel();
  };
}, [debouncedSearch]);


useEffect(() => {
  if (period && date) {
    getOrderReport(period, date);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [search]);


  return (
    <RootContainer>
      <Title>{t('order_report')}</Title>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Radio.Group onChange={handleStatusChange} value={period}>
            {period_options.map((period) => (
              <Radio.Button value={period.key}>{period.label}</Radio.Button>
            ))}
          </Radio.Group>
        </Col>
        <Col xs={4} />
        <Col xs={24} md={8} style={{ textAlign: "right" }}>
          <DatePicker
            allowClear={false}
            value={date}
            format={
              period === DAILY
                ? dateFormat
                : period === MONTHLY
                ? monthFormat
                : yearFormat
            }
            picker={
              period === DAILY ? "date" : period === MONTHLY ? "month" : "year"
            }
            onChange={handleDateChange}
          />
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('total_orders')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.totalCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('pending_order')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.pendingCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('unpaid_order')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.unpaidCount || 0)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('confirmed_order')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.confirmedCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('complete_order')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.completedCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Text>{t('cancelled_order')}</Text>
            <BoldText>
              {loading ? "..." : getFormattedNumber(counts.cancelledCount)}
            </BoldText>
          </Card>
        </Col>
         <Col xs={24} md={6}>
         {period === DAILY && (
            <Input
            placeholder="Search by order id"
            prefix={<IconSearch />}
            onChange={(e) => debouncedSearch(e.target.value)}
            allowClear
          />
            )}
        </Col>
        <Col xs={24}>
          <Table
            dataSource={data}
            columns={columns}
            loading={loading}
            pagination={false}
            scroll={{x : 900}}
          />
        </Col>
      </Row>
    </RootContainer>
  );
};

const Entry = () => {
  return (
    <Routes>
      <Route path="" element={<OrderReport />} />
      <Route path="/details/:id" element={<OrderDetails />} />
    </Routes>
  );
};

export default Entry;
